import React, { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { MarkdownEditor } from '@/components/ui/markdown-editor';
import { Release, Status, Environment, SubItem } from '@/types';
import { useForm } from 'react-hook-form';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { NativeSelect } from '@/components/ui/native-select';
import { NativeDatePicker } from '@/components/ui/native-date';

interface InlineReleaseFormProps {
  release?: Release;
  onSubmit: (release: Omit<Release, 'id'>) => void;
  onCancel: () => void;
}

// Form data type that matches what we need for the form
type ReleaseFormData = {
  status: Status;
  environment: Environment;
  brdLink: string;
  approvedBy: string;
  prework: string;
  notes: string;
  plannedReleaseDate?: Date;
  clientId: string;
  ownerId?: string;
  subItems: SubItem[];
  version: string;
};

const InlineReleaseForm: React.FC<InlineReleaseFormProps> = ({
  release,
  onSubmit,
  onCancel
}) => {
  const form = useForm<ReleaseFormData>({
    defaultValues: release
      ? {
          status: release.status,
          environment: release.environment || 'staging',
          brdLink: release.brdLink || '',
          approvedBy: release.approvedBy || '',
          prework: release.prework || '',
          notes: release.notes || '',
          plannedReleaseDate: release.plannedReleaseDate,
          clientId: release.clientId || '',
          ownerId: release.ownerId,
          subItems: release.subItems || [],
          version: release.version || '1.0.0',
        }
      : {
          status: 'not ready' as Status,
          environment: 'staging' as Environment,
          brdLink: 'To Be Added',
          approvedBy: 'To Be Added',
          prework: 'To Be Added',
          notes: '',
          plannedReleaseDate: undefined,
          clientId: '',
          ownerId: '',
          subItems: [],
          version: '1.0.0',
        },
  });

  // Watch for status changes and update environment accordingly
  const status = form.watch('status');
  useEffect(() => {
    if (status === 'released') {
      form.setValue('environment', 'live');
    }
  }, [status, form]);

  const handleSubmit = (values: ReleaseFormData) => {
    // Create the release object
    const submittedRelease: Omit<Release, 'id'> = {
      status: values.status,
      environment: values.environment,
      brdLink: values.brdLink,
      approvedBy: values.approvedBy,
      prework: values.prework,
      notes: values.notes,
      plannedReleaseDate: values.plannedReleaseDate,
      // Ensure clientId is properly set - use the existing one from the release prop if available
      clientId: release?.clientId || values.clientId,
      ownerId: values.ownerId,
      subItems: values.subItems,
      version: values.version,
      createdAt: release?.createdAt || new Date(),
    };
    
    console.log('[InlineReleaseForm] Submitting release with clientId:', submittedRelease.clientId);
    onSubmit(submittedRelease);
    
    // Reset form after submission if it's a new release
    if (!release) {
      form.reset({
        status: 'not ready' as Status,
        environment: 'staging' as Environment,
        brdLink: '',
        approvedBy: '',
        prework: '',
        notes: '',
        plannedReleaseDate: undefined,
        clientId: '',
        ownerId: '',
        subItems: [],
        version: '1.0.0',
      });
    }
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold">{release ? 'Edit Release' : 'Create New Release'}</h2>
            <p className="text-sm text-gray-500">
              {release ? 'Update release details' : 'Add a new release to track changes'}
            </p>
          </div>
        </div>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="version"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Version</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="e.g. 1.0.0, 1.0, or 1" 
                    {...field} 
                    pattern="^\d+(\.\d+(\.\d+)?)?$"
                    title="Enter version as: full number (1), decimal (1.0), or semantic version (1.0.0)"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <FormControl>
                    <NativeSelect
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      placeholder="Select status"
                      options={[
                        { value: "not ready", label: "Not Ready" },
                        { value: "ready for release", label: "Ready for Release" },
                        { value: "released", label: "Released" },
                        { value: "on hold", label: "On Hold" }
                      ]}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="environment"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Environment</FormLabel>
                  <FormControl>
                    <NativeSelect
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      placeholder="Select environment"
                      options={[
                        { value: "staging", label: "Staging" },
                        { value: "live", label: "Live" }
                      ]}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            </div>

            <FormField
              control={form.control}
              name="brdLink"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>BRD Link</FormLabel>
                  <FormControl>
                    <Input placeholder="Link to business requirements document" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="approvedBy"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Approved By</FormLabel>
                  <FormControl>
                    <Input placeholder="Who approved this release" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="prework"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pre-Work Site Check by</FormLabel>
                  <p className="text-sm text-muted-foreground mb-2">
                    Check the site for outstanding updates the client may have made. Check the pages of the site to ensure they look as expected. If there are updates or broken pages, advise the initiator of the work.
                  </p>
                  <FormControl>
                    <Input placeholder="Who performed the pre-work site check" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="plannedReleaseDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Planned Release Date</FormLabel>
                  <FormControl>
                    <NativeDatePicker
                      value={field.value}
                      onChange={(date) => field.onChange(date)}
                      min={new Date().toISOString().split('T')[0]} // Today as minimum date
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <MarkdownEditor 
                      value={field.value} 
                      onChange={field.onChange}
                      placeholder="Additional notes about this release (supports markdown formatting)" 
                      minHeight="150px"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-4 mt-8">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button type="submit">
                {release ? 'Update Release' : 'Create Release'}
              </Button>
            </div>
          </form>
        </Form>
        <div className="flex justify-end mt-4 pt-4 border-t">
          <div className="text-sm text-gray-500">
            {release ? 'Editing existing release' : 'Creating new release'}
          </div>
        </div>
    </div>
  );
};

export default InlineReleaseForm;
